import type { ProblemDetails } from './contracts'

const apiOrigin = (import.meta.env.VITE_API_ORIGIN as string | undefined)?.replace(/\/$/, '') ?? ''
let csrfToken: string | null = null

export class ApiError extends Error {
  readonly status: number
  readonly problem: ProblemDetails

  constructor(status: number, problem: ProblemDetails) {
    super(problem.detail ?? problem.title ?? `Request failed (${status})`)
    this.name = 'ApiError'
    this.status = status
    this.problem = problem
  }
}

export function setCsrfToken(token: string | null) {
  csrfToken = token
}

function isMutation(method: string) {
  return !['GET', 'HEAD', 'OPTIONS'].includes(method)
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase()
  const headers = new Headers(init.headers)
  if (init.body && !(init.body instanceof FormData) && !headers.has('content-type')) {
    headers.set('content-type', 'application/json')
  }
  if (isMutation(method) && csrfToken) headers.set('x-csrf-token', csrfToken)
  headers.set('accept', 'application/json, application/problem+json')

  const response = await fetch(`${apiOrigin}${path}`, {
    ...init,
    method,
    headers,
    credentials: 'include',
    cache: 'no-store',
  })
  if (!response.ok) {
    const problem = (await response.json().catch(() => ({
      title: response.statusText,
      status: response.status,
    }))) as ProblemDetails
    throw new ApiError(response.status, problem)
  }
  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

export function jsonBody(value: unknown): Pick<RequestInit, 'body' | 'headers'> {
  return {
    body: JSON.stringify(value),
    headers: { 'content-type': 'application/json' },
  }
}

export async function fetchEvidenceContent(id: string, signal?: AbortSignal) {
  const response = await fetch(`${apiOrigin}/api/v1/evidence/${id}/content`, {
    credentials: 'include',
    cache: 'no-store',
    signal,
  })
  if (!response.ok) {
    const problem = (await response.json().catch(() => ({
      title: 'Document unavailable',
      detail: 'The document could not be opened. Check its scan status and your permissions.',
    }))) as ProblemDetails
    throw new ApiError(response.status, problem)
  }
  return response.blob()
}

export async function downloadEvidence(id: string, filename: string) {
  const url = URL.createObjectURL(await fetchEvidenceContent(id))
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.rel = 'noopener'
  anchor.click()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

export function problemMessage(error: unknown) {
  if (error instanceof ApiError) return error.message
  if (error instanceof Error) return error.message
  return 'Something went wrong. Please try again.'
}
